import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { ApiError, getWebInstallationId } from '../../api/client';
import type { WebEmailLoginRequest } from '../../api/contract';
import { authMutation, createAuthRequestKey } from './api';
import { safeReturnTo } from './returnTo';
import { useYandexSignIn } from './useYandexSignIn';
import './auth.css';

// Public and reusable by email action pages; never reads AppDataProvider or /me.
export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const yandex = useYandexSignIn({ returnTo });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState<'email' | 'password'>();
  const [retryAt, setRetryAt] = useState(0);
  const emailInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);
  const attempt = useRef<{ email: string; password: string; key: string } | undefined>(undefined);
  const submitting = useRef(false);

  useEffect(() => {
    document.title = 'Войти в Казну — Казна';
  }, []);
  useEffect(() => {
    if (retryAt === 0) return;
    const timeout = window.setTimeout(() => setRetryAt(0), Math.min(Math.max(0, retryAt - Date.now()), 2_147_483_647));
    return () => window.clearTimeout(timeout);
  }, [retryAt]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current || yandex.starting || retryAt > Date.now()) return;
    yandex.clearError();
    setInvalid(undefined);
    if (!email) {
      setInvalid('email'); setError('Введите адрес электронной почты.'); emailInput.current?.focus(); return;
    }
    if (!password) {
      setInvalid('password'); setError('Введите пароль.'); passwordInput.current?.focus(); return;
    }
    if (attempt.current?.email !== email || attempt.current.password !== password) {
      attempt.current = { email, password, key: createAuthRequestKey() };
    }
    submitting.current = true;
    setPending(true); setError('');
    try {
      const body: WebEmailLoginRequest = { email, password, installationId: getWebInstallationId(), appVersion: 'kazna-web/0.1.0' };
      await authMutation<void>('/auth/web/email/login', body, attempt.current.key);
      attempt.current = undefined;
      setPassword('');
      navigate(returnTo, { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 0) {
        setUncertain(true);
        setError('Не удалось подтвердить вход. Финансовые данные скрыты. Повторите вход с теми же данными.');
      } else if (caught instanceof ApiError && caught.code === 'UNAUTHENTICATED') {
        // The rejection permits correction, which gets a fresh memory key.
        // The coordinator stays unknown until a later confirmed login.
        setUncertain(false);
        setError('Почта или пароль не подходят. Проверьте данные и повторите вход.');
      } else {
        setError(caught instanceof Error ? caught.message : 'Не удалось войти. Повторите попытку.');
      }
      if (caught instanceof ApiError && caught.retryAfterMs !== undefined) setRetryAt(Date.now() + caught.retryAfterMs);
    } finally {
      submitting.current = false; setPending(false);
    }
  };

  return (
    <main className="login-page public-auth-page email-login-page">
      <section className="login-card" aria-labelledby="email-login-title">
        <header className="auth-login-header">
          <img className="brand-mark" src="/icon.svg" alt="" aria-hidden="true" />
          <h1 id="email-login-title">Войти в Казну</h1>
        </header>
        {params.get('accountDeleted') === '1' && <p role="status">Удаление аккаунта запрошено. Финансовые данные закрыты.</p>}
        <form className="form" noValidate onSubmit={event => void submit(event)} aria-busy={pending || yandex.starting}>
          <label className="field" htmlFor="login-email">
            <span>Электронная почта</span>
            <input ref={emailInput} id="login-email" name="email" type="text" inputMode="email" autoComplete="username" autoCapitalize="none" spellCheck={false}
              value={email} readOnly={pending || uncertain || yandex.starting} required aria-invalid={invalid === 'email'} aria-describedby={error ? 'login-error' : undefined}
              onChange={event => { setEmail(event.target.value); attempt.current = undefined; }} />
          </label>
          <div className="field">
            <div className="auth-password-label">
              <label htmlFor="login-password">Пароль</label>
              <Link className="auth-text-link" to={`/auth/email/reset${returnTo === '/dashboard' ? '' : `?returnTo=${encodeURIComponent(returnTo)}`}`}>Забыли пароль?</Link>
            </div>
            <div className="auth-password">
              <input ref={passwordInput} id="login-password" name="password" type={visible ? 'text' : 'password'} autoComplete="current-password"
                value={password} readOnly={pending || uncertain || yandex.starting} required aria-invalid={invalid === 'password'} aria-describedby={error ? 'login-error' : undefined}
                onChange={event => { setPassword(event.target.value); attempt.current = undefined; }} />
              <button type="button" className="auth-password-toggle" aria-label={visible ? 'Скрыть пароль' : 'Показать пароль'} aria-pressed={visible} onClick={() => setVisible(value => !value)}>
                {visible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          {error && <p id="login-error" className="error" role="alert">{error}</p>}
          {retryAt > 0 && <p role="status">Слишком много попыток. Дождитесь, когда кнопка входа станет доступна.</p>}
          <button className="button button-primary wide" type="submit" disabled={pending || yandex.starting || retryAt > 0}>
            {pending ? 'Входим…' : uncertain ? 'Повторить вход' : 'Войти'}
          </button>
          <button className="button button-secondary wide" type="button" disabled={pending || uncertain || yandex.starting}
            onClick={() => {
              if (submitting.current || uncertain) return;
              submitting.current = true;
              setError('');
              void yandex.start().finally(() => { submitting.current = false; });
            }}>
            {yandex.starting ? 'Открываем Яндекс ID…' : 'Войти через Яндекс ID'}
          </button>
          {yandex.error && <p className="error" role="alert">{yandex.error}</p>}
          <p className="auth-register-link">Нет аккаунта? <Link className="auth-text-link" to={`/auth/email/register${returnTo === '/dashboard' ? '' : `?returnTo=${encodeURIComponent(returnTo)}`}`}>Зарегистрироваться</Link></p>
        </form>
      </section>
    </main>
  );
}
